/**
 * An event's own web page — the address a committee pastes onto an event so
 * members can reach the race site, the registration form or the timing
 * partner. Twin of `apps/api/src/shared/link.ts`.
 *
 * Two jobs, and the second one is a security rule rather than a nicety:
 *
 * 1. People paste `tirupurmarathon.in/register`, not `https://…`. Refusing
 *    that would teach the committee the field is broken. We add the scheme.
 * 2. This value ends up in an `href`. `javascript:` and `data:` addresses in
 *    an href are script the club did not write, so ONLY http and https are
 *    allowed through — checked here, on the way in, not at render time where
 *    a future screen could forget.
 */

/** Trimmed, with `https://` added when the address carries no scheme at all. */
export function normalizeEventLink(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  // Only when there is NO scheme. `javascript:alert(1)` keeps its scheme and
  // is refused below — prefixing it would hide the very thing we check for.
  return /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
}

/** The problem with a NORMALISED link, or null when it is safe to publish. */
export function eventLinkError(link) {
  if (!link) return null; // no link is the normal case
  if (link.length > 500) return 'That link is too long.';
  let url;
  try {
    url = new URL(link);
  } catch {
    return 'That does not look like a web address.';
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return 'An event link has to start with http:// or https://.';
  }
  if (!url.hostname.includes('.') || url.hostname.endsWith('.')) {
    return 'That web address has no domain — try tirupurmarathon.in/register.';
  }
  return null;
}

/**
 * What a link should READ as on a card: the host without `www.`, so a row
 * says `tirupurmarathon.in` rather than 90 characters of query string.
 * Returns '' for anything that is not a valid link — callers render the
 * label only when there is one.
 */
export function eventLinkLabel(link) {
  if (!link || eventLinkError(link)) return '';
  try {
    return new URL(link).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
}
